/**
 * MaintenanceFormWizard.jsx
 * Formulario de Manutencao em Etapas Premium
 *
 * 3 Passos:
 * 1. Basico - Nome, Tipo, Inicio, Periodo (obrigatorio)
 * 2. Atributos - Atributos customizados (opcional)
 * 3. Revisao
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
 Box, Grid, Typography, FormControl, InputLabel, Select, MenuItem 
} from '@mui/material';
import dayjs from 'dayjs';
import {
  NewReleases as MaintenanceIcon,
  Tune as TuneIcon,
  CheckCircle as ReviewIcon,
} from '@mui/icons-material';

import { useTranslation } from '../../common/components/LocalizationProvider';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useAttributePreference } from '../../common/util/preferences';
import {
  speedFromKnots, speedToKnots, distanceFromMeters, distanceToMeters,
} from '../../common/util/converter';
import { prefixString } from '../../common/util/stringUtils';
import usePositionAttributes from '../../common/attributes/usePositionAttributes';

import PremiumWizard from '../../common/components/premium/PremiumWizard';
import PremiumInput from '../../common/components/premium/PremiumInput';
import EditAttributesAccordion from './EditAttributesAccordion';
import { getWizardColors } from '../../common/components/premium/WizardStepTemplates';

// ============================================================================
// STEP 1: INFORMACOES BASICAS
// ============================================================================

const BasicInfoStep = ({ data, updateData, errors }) => {
  const t = useTranslation();
  const positionAttributes = usePositionAttributes(t);
  const speedUnit = useAttributePreference('speedUnit', 'kn');
  const distanceUnit = useAttributePreference('distanceUnit', 'km');

  const [labels, setLabels] = useState({ start: '', period: '' });

  const convertToList = (attributes) => {
    const otherList = [];
    Object.keys(attributes).forEach((key) => {
      const value = attributes[key];
      if (value.type === 'number' || key.endsWith('Time')) {
        otherList.push({ key, name: value.name, type: value.type });
      }
    });
    return otherList;
  };

  useEffect(() => {
    const attribute = positionAttributes[data?.type];
    if (data?.type?.endsWith('Time')) {
      setLabels({ start: null, period: t('sharedDays') });
    } else if (attribute && attribute.dataType) {
      switch (attribute.dataType) {
        case 'speed':
          setLabels({ start: t(prefixString('shared', speedUnit)), period: t(prefixString('shared', speedUnit)) });
          break;
        case 'distance':
          setLabels({ start: t(prefixString('shared', distanceUnit)), period: t(prefixString('shared', distanceUnit)) });
          break;
        case 'hours':
          setLabels({ start: t('sharedHours'), period: t('sharedHours') });
          break;
        default:
          setLabels({ start: null, period: null });
          break;
      }
    } else {
      setLabels({ start: null, period: null });
    }
  }, [data?.type, positionAttributes, speedUnit, distanceUnit, t]);

  const rawToValue = (start, value) => {
    const attribute = positionAttributes[data?.type];
    if (data?.type?.endsWith('Time')) {
      if (start) {
        return dayjs(value).locale('en').format('YYYY-MM-DD');
      }
      return value / 86400000;
    }
    if (attribute && attribute.dataType) {
      switch (attribute.dataType) {
        case 'speed':
          return speedFromKnots(value, speedUnit);
        case 'distance':
          return distanceFromMeters(value, distanceUnit);
        case 'hours':
          return value / 3600000;
        default:
          return value;
      }
    }
    return value;
  };

  const valueToRaw = (start, value) => {
    const attribute = positionAttributes[data?.type];
    if (data?.type?.endsWith('Time')) {
      if (start) {
        return dayjs(value, 'YYYY-MM-DD').valueOf();
      }
      return value * 86400000;
    }
    if (attribute && attribute.dataType) {
      switch (attribute.dataType) {
        case 'speed':
          return speedToKnots(value, speedUnit);
        case 'distance':
          return distanceToMeters(value, distanceUnit);
        case 'hours':
          return value * 3600000;
        default:
          return value;
      }
    }
    return value;
  };

  const handleTypeChange = (e) => {
    updateData({ type: e.target.value, start: 0, period: 0 });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PremiumInput
            label={`${t('sharedName')} *`}
            value={data.name || ''}
            onChange={(e) => updateData({ name: e.target.value })}
            error={!!errors?.name}
            helperText={errors?.name}
            autoFocus
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small" className="premium-input" error={!!errors?.type}>
            <InputLabel>{`${t('sharedType')} *`}</InputLabel>
            <Select
              label={`${t('sharedType')} *`}
              value={data.type || ''}
              onChange={handleTypeChange}
            >
              {convertToList(positionAttributes).map(({ key, name }) => (
                <MenuItem key={key} value={key}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PremiumInput
            type={data.type?.endsWith('Time') ? 'date' : 'number'}
            value={rawToValue(true, data.start) || ''}
            onChange={(e) => updateData({ start: valueToRaw(true, e.target.value) })}
            label={labels.start ? `${t('maintenanceStart')} * (${labels.start})` : `${t('maintenanceStart')} *`}
            error={!!errors?.start}
            helperText={errors?.start}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PremiumInput
            type="number"
            value={rawToValue(false, data.period) || ''}
            onChange={(e) => updateData({ period: valueToRaw(false, e.target.value) })}
            label={labels.period ? `${t('maintenancePeriod')} * (${labels.period})` : `${t('maintenancePeriod')} *`}
            error={!!errors?.period}
            helperText={errors?.period}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

// ============================================================================
// STEP 2: ATRIBUTOS
// ============================================================================

const AttributesStep = ({ data, updateData }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          p: 2,
          borderRadius: '10px',
          backgroundColor: colors.surfaceHover,
          border: `1px solid ${colors.borderSubtle}`,
        }}
      >
        <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '13px' }}>
          {t('wizardAttributesHelp')}
        </Typography>
      </Box>

      <EditAttributesAccordion
        attributes={data.attributes}
        setAttributes={(attributes) => updateData({ attributes })}
        definitions={{}}
      />
    </Box>
  );
};

// ============================================================================
// STEP 3: REVISAO
// ============================================================================

const ReviewStep = ({ data }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);
  const positionAttributes = usePositionAttributes(t);

  const InfoRow = ({ label, value }) => {
    if (!value && value !== 0) return null;
    return (
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '11px' }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: colors.textPrimary, fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}
        >
          {value}
        </Typography>
      </Box>
    );
  };

  const typeName = data.type ? positionAttributes[data.type]?.name || data.type : '-';
  const attributeCount = data.attributes ? Object.keys(data.attributes).length : 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          backgroundColor: colors.accentSubtle,
          borderRadius: tokens?.radius?.md || '8px',
          border: `1px solid ${colors.accent}30`,
        }}
      >
        <ReviewIcon sx={{ color: colors.accent }} />
        <Typography sx={{ color: colors.textPrimary }}>
          {t('wizardReviewConfirm')}
        </Typography>
      </Box>

      <Box
        sx={{
          backgroundColor: colors.surfaceElevated,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${colors.borderSubtle}`,
          p: 2.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: colors.textPrimary,
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '11px',
            letterSpacing: '0.05em',
            mb: 2,
          }}
        >
          {t('sharedRequired')}
        </Typography>
        <InfoRow label={t('sharedName')} value={data.name} />
        <InfoRow label={t('sharedType')} value={typeName} />
        <InfoRow label={t('maintenanceStart')} value={data.start ? String(data.start) : '-'} />
        <InfoRow label={t('maintenancePeriod')} value={data.period ? String(data.period) : '-'} />
        <InfoRow label={t('sharedAttributes')} value={attributeCount > 0 ? `${attributeCount} ${t('sharedAttributes').toLowerCase()}` : '-'} />
      </Box>
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const MaintenanceFormWizard = ({ maintenance, onSave, onCancel }) => {
  const t = useTranslation();
  const navigate = useNavigate();

  const steps = [
    {
      label: t('wizardStepBasic'),
      title: t('sharedRequired'),
      description: t('wizardMaintenanceBasicDescription') || 'Dados da manutencao',
      icon: <MaintenanceIcon sx={{ fontSize: 18 }} />,
      component: BasicInfoStep,
      validate: (data) => {
        const errors = {};
        if (!data.name?.trim()) errors.name = t('sharedRequired');
        if (!data.type) errors.type = t('sharedRequired');
        if (!data.start && data.start !== 0) errors.start = t('sharedRequired');
        if (!data.period) errors.period = t('sharedRequired');
        return Object.keys(errors).length ? errors : true;
      },
      getSummary: (data) => [
        { label: t('sharedName'), value: data.name },
        { label: t('sharedType'), value: data.type },
      ],
    },
    {
      label: t('sharedAttributes'),
      title: t('sharedAttributes'),
      description: t('wizardAttributesDescription'),
      icon: <TuneIcon sx={{ fontSize: 18 }} />,
      component: AttributesStep,
      optional: true,
    },
    {
      label: t('wizardStepReview'),
      title: t('wizardReviewTitle'),
      description: t('wizardReviewDescription'),
      icon: <ReviewIcon sx={{ fontSize: 18 }} />,
      component: ReviewStep,
    },
  ];

  const handleComplete = async (data) => {
    try {
      const url = maintenance?.id ? `/api/maintenance/${maintenance.id}` : '/api/maintenance';
      const method = maintenance?.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const saved = await response.json();
        onSave?.(saved);
        navigate('/settings/maintenances');
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || t('errorGeneric'));
      }
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    navigate('/settings/maintenances');
  };

  const initialData = maintenance || {
    name: '',
    type: '',
    start: 0,
    period: 0,
    attributes: {},
  };

  return (
    <PremiumWizard
      steps={steps}
      initialData={initialData}
      onComplete={handleComplete}
      onCancel={handleCancel}
      storageKey={maintenance?.id ? null : 'maintenance-wizard-draft'}
      completeButtonText={maintenance?.id ? t('sharedSave') : t('sharedAdd')}
      nextButtonText={t('wizardNext')}
      backButtonText={t('sharedBack')}
      cancelButtonText={t('sharedCancel')}
      skipButtonText={t('wizardSkip')}
      showSummary
    />
  );
};

export default MaintenanceFormWizard;
